import {
  all,
  call,
  put,
  takeLatest,
  takeEvery,
  select,
  fork,
  take,
  cancel,
  race,
} from 'redux-saga/effects';
import {
  DEFAULT_TASK_PRE_FILTER_KEYS,
  // TASK_STATUS,
  TASK_VIEW_MODES,
  VIEW_MODE_TYPE_LANES_MAPPING,
} from '../../../common/constants/TaskConstants';
import { getTasksApiHelper } from './taskSaga';
import {
  CANCEL_REFRESH_TASK,
  editBoardTask,
  editTask,
  fetchTaskForBoardView,
  refreshTaskForBoardView,
} from '../../routines/taskRoutines';
import { FILTER_NAME } from '../../../common/constants/FilterListConstants';
import { GTM_TRACK_EVENT } from '../../../common/GoogleTagManager/GoogleTagManager';
import { GTM_EVENTS } from '../../../common/GoogleTagManager/GTMEventConstants';
import { request } from '../../../common/util/RestClient';
// import SUCCESS_MESSAGES from '../../../common/constants/SuccessMessagesConstants';
import envConfig from '../../../config';

const PM_URL = `${envConfig.APIG_URL}project-mgt/tasks`;

const getBoardFilter = (values, key) => {
  return {
    attribute: {
      dataType: 'tags',
      displayName: key.replace(/^./, key[0].toUpperCase()),
      name: key.toLowerCase(),
    },
    value: values,
    filter: FILTER_NAME.IS_SET_TO,
    key,
  };
};

function* fetchTask({ payload, key, payloadToUse }) {
  const response = yield call(getTasksApiHelper, payload);
  yield put(
    fetchTaskForBoardView.success({
      response: response.data,
      currentPayload: payloadToUse,
      key,
    })
  );
}

export function* fetchTaskForBoardSaga({ payload }) {
  const { currentPayload: prevPayload, needToLoad } = yield select(
    (state) => state.taskBoard
  );

  const payloadToUse = payload?.usePrevPayload ? prevPayload : payload;
  const { boardViewType, filters = [], ..._payload } = payloadToUse;
  try {
    yield all(
      needToLoad.map((key) => {
        const _filters = [...filters, getBoardFilter([key], boardViewType)];
        const preKeyValue = _filters.find((filter) =>
          DEFAULT_TASK_PRE_FILTER_KEYS.includes(filter.key)
        );
        return call(fetchTask, {
          payload: {
            ..._payload,
            sortColumn: 'taskOrder',
            sortDirection: 'ASC',
            filters: _filters,
            isPreFilterKeyAvailable: !!preKeyValue,
          },
          key,
          payloadToUse,
        });
      })
    );
  } catch (error) {
    yield put(fetchTaskForBoardView.failure({ error }));
  } finally {
    yield put(fetchTaskForBoardView.fulfill());
  }
}

export function* editBoardTaskSaga({ payload }) {
  try {
    GTM_TRACK_EVENT(GTM_EVENTS.ACTIONS_TASKS_UPDATED);
    const { taskId, status, priority, taskOrder, viewModeType, onSuccess } =
      payload;

    const PAYLOAD_MAP = {
      [TASK_VIEW_MODES.BOARD_STATUS]: 'status',
      [TASK_VIEW_MODES.BOARD_PRIORITY]: 'priority',
    };
    // if we receive a put task order request, we need to cancel the refresh task saga otherwise it will mess the UI
    yield put({ type: CANCEL_REFRESH_TASK });
    yield call(request, `${PM_URL}/${taskId}`, {
      method: 'PATCH',
      payload: {
        [PAYLOAD_MAP[viewModeType]]:
          viewModeType === TASK_VIEW_MODES.BOARD_STATUS ? status : priority,
        taskOrder,
      },
    });

    if (onSuccess) {
      onSuccess();
    }
  } catch (error) {
    yield put(editTask.failure({ error }));
  } finally {
    yield put(editTask.fulfill());
  }
}

function* refreshTask({ payload, key }) {
  const response = yield call(getTasksApiHelper, payload);
  return { key, response };
}

function* refreshTaskForBoardSaga() {
  const { currentPayload, paginationData } = yield select(
    (state) => state.taskBoard
  );

  const payload = {
    ...currentPayload,
    page: 1,
    pageSize: paginationData.pageSize * paginationData.page,
  };

  const response = yield all(
    Object.values(
      VIEW_MODE_TYPE_LANES_MAPPING[currentPayload.boardViewType]
    ).map(({ key }) =>
      call(refreshTask, {
        payload: {
          ...payload,
          sortColumn: 'taskOrder',
          sortDirection: 'ASC',
          filters: [
            ...payload.filters,
            getBoardFilter([key], currentPayload.boardViewType),
          ],
        },
        key,
      })
    )
  );

  yield put(
    refreshTaskForBoardView.success({
      response,
    })
  );
}

function* refreshTaskForBoardSagaWrapper(action) {
  const task = yield fork(refreshTaskForBoardSaga, action);
  // if we receive a refresh cancel request, we need to cancel the refresh task request
  const result = yield race({
    completed: take([
      refreshTaskForBoardView.SUCCESS,
      refreshTaskForBoardView.FAILURE,
    ]),
    cancelled: take(CANCEL_REFRESH_TASK),
  });
  if (result.cancelled && task.isRunning()) {
    yield cancel(task);
  }
}

export default function* root() {
  yield all([
    takeEvery(refreshTaskForBoardView.TRIGGER, refreshTaskForBoardSagaWrapper),
    takeLatest(fetchTaskForBoardView.TRIGGER, fetchTaskForBoardSaga),
    takeEvery(editBoardTask.TRIGGER, editBoardTaskSaga),
  ]);
}
